import { z } from 'zod';

import { ComplexRoleUI } from '../../types/interfaces';

import { ScheduleWithDatesSchema } from './ScheduleSchema';
import { validateDateRangesOverlapWithAttainments, validateDateRangesOverlapWithDepartments } from './validation-helpers';

const standartLengthError = { message: 'До 50 символов' };
const phoneRegex = new RegExp(
  /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/
);
const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const loginRegex = /^[^\u0400-\u04FF]*$/;
const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

interface ITreeNode {
  key: string;
  label: string;
  fullName?: string;
  icon: string;
  children?: ITreeNode[];
  data?: string;
  surname?: string;
  name?: string;
  middleName?: string;
  organization?: string;
  userRole?: ComplexRoleUI[];
}

const RoleUISchema: z.ZodSchema<ComplexRoleUI> = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string(),
  roleUIs: z.array(z.any()).optional()
});

const TreeNodeSchema: z.ZodSchema<ITreeNode> = z.lazy(() =>
  z.object({
    key: z.string(),
    label: z.string(),
    fullName: z.string().optional(),
    icon: z.string(),
    children: z.array(z.lazy(() => TreeNodeSchema)).optional(),
    data: z.string().optional(),
    surname: z.string().optional(),
    name: z.string().optional(),
    middleName: z.string().optional(),
    organization: z.string().optional(),
    userRole: z.array(RoleUISchema).optional()
  })
);

const AttainmentWithDatesSchema = z.object({
  attainmentId: z.number(),
  name: z.string().optional(),
  startDate: z.string()
    .min(1, 'Укажите дату начала')
    .optional(),
  endDate: z.string()
    .min(1, 'Укажите дату окончания')
    .optional()
}).superRefine((attainment, ctx) => {

  if (attainment.attainmentId === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Выберите навык',
      path: ['attainmentId']
    });
  }
  
  if (attainment.startDate && attainment.endDate) {
    const start = new Date(attainment.startDate);
    const end = new Date(attainment.endDate);

    if (start > end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Дата начала не может быть позже даты окончания',
        path: ['generalDatesErrors', 'startDate']
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Дата окончания не может быть ранее даты начала',
        path: ['generalDatesErrors', 'endDate']
      });
    } else if (start.getTime() === end.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Дата начала и окончания не могут совпадать',
        path: ['generalDatesErrors', 'startDate']
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Дата начала и окончания не могут совпадать',
        path: ['generalDatesErrors', 'endDate']
      });
    }
  }
});

const ScheduleWithDatesSchemaExtension = z.object({
  departmentId: z.number().min(1, 'Обязательное поле')
});

const PersonSchedulesSchema = z.intersection(
  ScheduleWithDatesSchema,
  ScheduleWithDatesSchemaExtension
);

export const baseSchemaObject = {
  id: z.number().or(z.string()),
  personnelNumber: z
    .string()
    .max(50, standartLengthError)
    .nonempty({ message: 'Пожалуйста, укажите табельный номер' })
    .refine((value) => value.trim().length > 0, {
      message: 'Поле не может содержать только пробелы'
    })
    .or(z.number()),
  fullName: z.string(),
  surname: z
    .string()
    .nonempty({ message: 'Пожалуйста, укажите фамилию' })
    .max(50, standartLengthError)
    .refine((value) => value.trim().length > 0, {
      message: 'Поле не может содержать только пробелы'
    }),
  name: z
    .string()
    .nonempty({ message: 'Пожалуйста, укажите имя' })
    .max(50, standartLengthError)
    .refine((value) => value.trim().length > 0, {
      message: 'Поле не может содержать только пробелы'
    }),
  middleName: z.string().max(50, standartLengthError).optional(),
  phone: z
    .string()
    .regex(phoneRegex, 'Некорректный номер!')
    .min(11, { message: 'Минимальная длина 11 символов' })
    .max(12, { message: 'Максимальная длина 12 символов' })
    .or(z.number())
    .or(z.string().max(0).optional()),
  email: z
    .string()
    .email({ message: 'Некорректный email' })
    .refine(
      (value) => emailRegex.test(value),
      'Email может содержать только латинские символы, числа, точки, дефисы и нижнее подчеркивание'
    ),
  productivity: z.number()
    .or(z.string())
    .optional()
    .refine((val) => {
      if (val === undefined || val === '') return true;
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return !isNaN(num) && num >= 0;
    }, { message: 'Производительность не может быть отрицательной' }),
  isCustomProductivity: z.boolean().optional(),
  isUser: z.boolean(),
  login: z.string().max(50, standartLengthError).optional(),
  password: z.string().or(z.literal('')).optional(),
  userRole: z.array(RoleUISchema).optional(),
  requirePassword: z.boolean().default(false),
  personSchedules: z.array(PersonSchedulesSchema).optional(),
  attainments: z.array(AttainmentWithDatesSchema).optional(),
  startLongitude: z.number().nullable().optional(),
  startLatitude: z.number().nullable().optional(),
};

const EmployeeSchema = z.object(baseSchemaObject).superRefine((data, ctx) => {
  const allAttainments = data.attainments || [];

  if (allAttainments && allAttainments.length > 0) {
    const attainmentGroups = new Map<number, Array<{attainment: any; index: number}>>();
    
    allAttainments.forEach((attainment, index) => {
      if (!attainmentGroups.has(attainment.attainmentId)) {
        attainmentGroups.set(attainment.attainmentId, []);
      }
      attainmentGroups.get(attainment.attainmentId)!.push({ attainment, index });
    });

    attainmentGroups.forEach((group) => {
      if (group.length > 1) {
        const skillsWithoutPeriod = group.filter(
          item => !item.attainment.startDate && !item.attainment.endDate
        );

        if (skillsWithoutPeriod.length > 1) {
          skillsWithoutPeriod.forEach(item => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Нельзя добавлять одинаковый навык без периода действия более одного раза',
              path: ['attainments', item.index, 'attainmentId']
            });
          });
        }
      }
    });

    if (allAttainments.length > 1) {
      validateDateRangesOverlapWithAttainments(
        allAttainments,
        ctx,
        'attainments',
        'Периоды действия навыка пересекаются с другим периодом этого же навыка'
      );
    }
  }
  
  if (!data.isUser) {
    if (data.password) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Пароль не требуется, если пользователь не активен.',
        path: ['password']
      });
    }
  }

  if (data.isUser) {
    if (!data.login || data.login.trim().length === 0) {
      const errorMessage =
        !data.login || data.login.length === 0
          ? 'Укажите логин.'
          : 'Поле "Логин" не может содержать только пробелы.';
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage,
        path: ['login']
      });
    } else if (!loginRegex.test(data.login)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Логин не должен содержать кириллицу',
        path: ['password']
      });
    }
  }

  if (data.requirePassword) {
    if (!data.password || data.password.length < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Пароль обязателен и должен содержать не менее 8 символов.',
        path: ['password']
      });
    } else if (data.password && data.password.length > 20) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Максимальная длина пароля составляет 20 символов.',
        path: ['password']
      });
    } else if (!passwordRegex.test(data.password)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Пароль должен содержать латинские буквы (строчные и заглавные), цифры и спецсимволы',
        path: ['password']
      });
    }
  }

  if (data.personSchedules && data.personSchedules.length > 1) {
    validateDateRangesOverlapWithDepartments(
      data.personSchedules,
      ctx,
      'personSchedules',
      'Даты пересекаются с другим расписанием'
    );
  }

  const hasLongitude = data.startLongitude !== undefined && 
                       data.startLongitude !== null;
  const hasLatitude = data.startLatitude !== undefined && 
                      data.startLatitude !== null;

  if (hasLongitude && !hasLatitude) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Укажите широту',
      path: ['startLatitude']
    });
  }

  if (hasLatitude && !hasLongitude) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Укажите долготу',
      path: ['startLongitude']
    });
  }

  if (hasLongitude) {
    if (data.startLongitude! < -180 || data.startLongitude! > 180) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Долгота должна быть в диапазоне от -180 до 180',
        path: ['startLongitude']
      });
    }
  }

  if (hasLatitude) {
    if (data.startLatitude! < -90 || data.startLatitude! > 90) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Широта должна быть в диапазоне от -90 до 90',
        path: ['startLatitude']
      });
    }
  }
});

export { EmployeeSchema };